import { FC, useEffect, useRef, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { ApolloError, useLazyQuery } from '@apollo/client';
import { useSearchParams } from 'next/navigation';
import { LocaleUtils } from 'react-day-picker';
import clsx from 'clsx';
import {
  Button,
  Calendar,
  DropDownModal,
  Heading,
  InlineMessages,
  InlineMessagesType,
  Types,
  getCurrentDateObject,
} from '@marriott/mi-ui-library';
import {
  AddHotelInput,
  AddHotelResponse,
  addHotelCorporateQuery,
  addHotelGroupQuery,
} from '@marriott/mi-groups-graphql';
import { PropertyCard } from './PropertyCard';
import { ReslinkPropertyCard } from '../ReslinkEventDetails.types';
import { TextFormField } from '../../../../../molecules';
import { DateObject } from '../../../../../molecules/DatePicker/DatePicker.types';
import {
  CALENDAR_SHORT_WEEKDAY_NAMES,
  CALENDAR_LONG_WEEKDAY_NAMES,
  CALENDAR_SHORT_MONTH_NAMES,
  CALENDAR_MAX_ALLOWED_DAYS,
  CALENDAR_START_DATE,
  ENTER_KEY,
  ALPHA_NUMERIC_REGEX,
  PROPERTY_IMAGE_LIST_CATEGORY_LIMIT,
  RESLINK_MAX_PROPERTY_COUNT,
  RESLINK_MAX_ROOM_COUNT,
} from '../../../../../constants';
import {
  getCurrentLocale,
  getFormattedCalendarDate,
  getFormattedDate,
  getFormattedDateObject,
  getLocaleValue,
  setCalendarOptions,
  updateDataLayerWithInventoryAttributes,
} from '../../../../../utils';
import { AddHotelProps, AddHotelForm, CalendarState } from './AddHotel.types';
import { StyledAddHotelForm } from './AddHotel.styles';
import { ModifyDatesOccupancyModal } from './ModifyDatesOccupancyModal';
import { ModifyDatesOccupancyData } from './ModifyDatesOccupancyModal/ModifyDatesOccupancyModal.types';
import { ReservationLinkType } from '../../DashboardContentTiles/ReslinkTypeModal/ReslinkTypeModal.types';
import { useLocaleStore } from '../../../../../store';
import { useReslinkAssociateStore } from '../../../../../admin/store';
import {
  getPropertyData,
  isGroupType,
  isCorporateType,
  hasInventoryInDateRange,
  getEventTrackingData,
} from '../../../../utils/reslink';
import { useFocusOut } from '../../../../../hooks';
import { InventoryCheckModal } from './InventoryCheckModal';
import { InventoryCheckData, UpdatedInventoryData } from './InventoryCheckModal/InventoryCheckModal.types';

export const AddHotel: FC<AddHotelProps> = (props: AddHotelProps) => {
  const { labels, control, errors, onError, isPickUpStatsEnabled } = props;
  const {
    heading,
    helperText,
    marshacode,
    minihotelcode,
    miniHotelStartDate,
    clusterCode,
    cta,
    removeHotelPopUp,
    propertyCard: propertyCardLabels,
    editHotelPopUp,
    maxRoomCardError,
    apiFailure,
    inventoryCheckPopUp,
  } = labels;
  const {
    shortWeekdayNames = CALENDAR_SHORT_WEEKDAY_NAMES,
    longWeekdayNames = CALENDAR_LONG_WEEKDAY_NAMES,
    shortMonthNames = CALENDAR_SHORT_MONTH_NAMES,
    longMonthNames = LocaleUtils.getMonths(),
  } = editHotelPopUp.dateLabels;
  const { cancelCtaLabel, confirmCtaLabel } = cta;

  const urlParams = useSearchParams();
  const reslinkType = urlParams.get('reslinkType') || '';

  const isGroup = isGroupType(reslinkType as ReservationLinkType);
  const isCorporate = isCorporateType(reslinkType as ReservationLinkType);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const calendarRef = useRef<any>(null);
  const datePickerInputRef = useRef<HTMLDivElement>(null);
  const dropDownRef = useRef<HTMLDivElement>(null);

  const { locale } = useLocaleStore();
  const { setEventTrackingData, setEventTrackingPropertyIndex, eventTrackingPropertyIndex } =
    useReslinkAssociateStore();

  const [calendarState, setCalendarState] = useState<CalendarState>({
    startDate: null,
    isFlyoutOpen: false,
  });
  const [showModifyDatesOccupancyModal, setShowModifyDatesOccupancyModal] = useState<boolean>(false);
  const [modifyDatesOccupancyData, setModifyDatesOccupancyData] = useState<ModifyDatesOccupancyData>({
    id: '',
    startDate: '',
    endDate: '',
    overriddenStartDate: '',
    overriddenEndDate: '',
    maxGuestsPerRoom: '',
  });
  const [apiError, setApiError] = useState(false);

  const [currentlyAddedHotel, setCurrentlyAddedHotel] = useState<AddHotelResponse>();
  const [showInventoryCheckModal, setShowInventoryCheckModal] = useState(false);
  const [inventoryCheckCount, setInventoryCheckCount] = useState(0);
  const [inventoryCheckData, setInventoryCheckData] = useState<InventoryCheckData>({
    startDate: '',
    endDate: '',
  });

  const {
    control: addHotelControl,
    trigger,
    getValues,
    setValue,
    reset,
    formState: { errors: addHotelErrors },
  } = useForm<AddHotelForm>({
    defaultValues: {
      marshaCode: '',
      miniHotelCode: '',
      miniHotelStartDate: '',
      clusterCode: '',
    },
  });

  const {
    fields: propertyCardsData,
    append,
    update,
    remove,
    swap,
  } = useFieldArray({
    name: 'propertyCards',
    control,
  });

  useEffect(() => {
    setCalendarOptions({
      shortWeekdayNames,
      longWeekdayNames,
      shortMonthNames,
      longMonthNames,
    });
  }, [shortWeekdayNames, longWeekdayNames, shortMonthNames, longMonthNames]);

  useEffect(() => {
    setCalendarState(prevState => ({ ...prevState, startDate: getCurrentDateObject() }));
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    if (!showInventoryCheckModal && params.has('inventoryCheck')) {
      params.delete('inventoryCheck');
      window.history.replaceState({}, '', `?${params.toString()}`);
      updateDataLayerWithInventoryAttributes(params.toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useFocusOut({
    ref: [datePickerInputRef.current, dropDownRef.current],
    onFocusOut: (isFocusOut: boolean) => {
      if (isFocusOut) {
        closeFlyout();
      }
    },
    handleKeyup: true,
  });

  const [addHotel, { loading }] = useLazyQuery<AddHotelResponse>(isGroup ? addHotelGroupQuery : addHotelCorporateQuery);

  const updateStartDate = (startDate: DateObject | null) => {
    setCalendarState(prevState => ({ ...prevState, startDate }));
    setValue('miniHotelStartDate', getFormattedDate(startDate));
    closeFlyout();
  };

  const toggleFlyout = () => setCalendarState(prevState => ({ ...prevState, isFlyoutOpen: !prevState.isFlyoutOpen }));

  const closeFlyout = () => setCalendarState(prevState => ({ ...prevState, isFlyoutOpen: false }));

  const handleAddHotel = async () => {
    setApiError(false);
    const fields: ('marshaCode' | 'miniHotelCode' | 'clusterCode')[] = isGroup
      ? ['marshaCode', 'miniHotelCode']
      : ['marshaCode', 'clusterCode'];
    const isValid = await trigger(fields);

    if (isValid) {
      const addHotelInput: AddHotelInput = {
        propertyId: getValues('marshaCode'),
        imageLimitPerCategory: PROPERTY_IMAGE_LIST_CATEGORY_LIMIT,
      };

      if (isGroup) {
        addHotelInput.input = {
          propertyCode: getValues('marshaCode'),
          miniHotelCode: getValues('miniHotelCode'),
          startDate: getFormattedCalendarDate(
            getValues('miniHotelStartDate') || getFormattedDate(getCurrentDateObject()),
            'hyphenatedDateWithMonthNoAndYear'
          ),
        };
      }

      addHotel({
        variables: addHotelInput,
        context: {
          headers: {
            'accept-language': locale,
            ...(isGroup && { 'uxl-cache-disable': true }),
          },
        },
        fetchPolicy: 'no-cache',
        onCompleted: addHotelResponse => {
          onError('');
          if (
            !addHotelResponse.property.basicInformation ||
            (isGroup && !addHotelResponse.searchMiniHotels?.edges.length)
          ) {
            window.scrollTo(0, 0);
            onError(marshacode.marshaCodeError);
          } else {
            const miniHotelData = addHotelResponse.searchMiniHotels?.edges[0].node;

            !isGroup ||
            !miniHotelData ||
            hasInventoryInDateRange({
              availability: miniHotelData.availability ?? [],
              startDate: miniHotelData.startDate ?? '',
              endDate: miniHotelData.endDate ?? '',
            })
              ? displayHotel(addHotelResponse)
              : checkInventoryAvailabilityWithNewDate(addHotelResponse);
          }
        },
        onError: (error: ApolloError) => {
          if (error.networkError) {
            setApiError(true);
          } else {
            window.scrollTo(0, 0);
            onError(isGroup ? marshacode.marshaCodeError : clusterCode.clusterCodeError);
          }
        },
      });
    }
  };

  const handleMoveUp = (index: number) => swap(index, index - 1);

  const handleMoveDown = (index: number) => swap(index, index + 1);

  const handleEdit = (propertyData: ReslinkPropertyCard & { id: string }) => {
    setModifyDatesOccupancyData({
      id: propertyData.id,
      startDate: propertyData?.fromDate || '',
      endDate: propertyData?.toDate || '',
      overriddenStartDate: propertyData?.overriddenStartDate,
      overriddenEndDate: propertyData?.overriddenEndDate,
      maxGuestsPerRoom:
        propertyData?.overriddenMaxOccupancy?.toString() || propertyData?.maxOccupancy?.toString() || '',
    });
    setShowModifyDatesOccupancyModal(true);
  };

  const updateDateAndOccupancy = ({
    id,
    overriddenStartDate,
    overriddenEndDate,
    maxGuestsPerRoom,
  }: ModifyDatesOccupancyData) => {
    const propertyIndex = propertyCardsData.findIndex(item => item?.id === id);

    if (propertyIndex !== -1) {
      const property = propertyCardsData[propertyIndex];

      update(propertyIndex, {
        ...property,
        overriddenStartDate,
        overriddenEndDate,
        overriddenMaxOccupancy: +maxGuestsPerRoom || (property?.overriddenMaxOccupancy as number),
        isEdited: true,
      });
    }
  };

  const propertyCardValidator = (property: ReslinkPropertyCard) => {
    if (isGroup) {
      const roomsCount = property.roomsData.filter(roomData => !roomData.isRemoved).length;
      if (roomsCount === 0) return propertyCardLabels.noGroupCodeError;
      if (roomsCount > RESLINK_MAX_ROOM_COUNT) return maxRoomCardError;
    }
    return true;
  };

  const checkInventoryAvailabilityWithNewDate = (addHotelResponse: AddHotelResponse) => {
    const addHotelMiniHotelData = addHotelResponse.searchMiniHotels?.edges[0].node;
    setInventoryCheckData({
      startDate: addHotelMiniHotelData?.startDate ?? '',
      endDate: addHotelMiniHotelData?.endDate ?? '',
    });
    setCurrentlyAddedHotel(addHotelResponse);
    setShowInventoryCheckModal(true);
    setInventoryCheckCount(count => count + 1);
  };

  const updateInventoryDateRange = ({ overriddenStartDate, overriddenEndDate }: UpdatedInventoryData) => {
    const updatedData: AddHotelResponse = {
      ...currentlyAddedHotel!,
      searchMiniHotels: {
        ...currentlyAddedHotel?.searchMiniHotels,
        edges:
          currentlyAddedHotel?.searchMiniHotels?.edges.map((edge, index) =>
            index === 0
              ? {
                  ...edge,
                  node: {
                    ...edge.node,
                    overriddenStartDate,
                    overriddenEndDate,
                  },
                }
              : edge
          ) ?? [],
      },
    };
    setCurrentlyAddedHotel(updatedData);
    setShowInventoryCheckModal(false);
    displayHotel(updatedData, { overriddenStartDate, overriddenEndDate });
  };

  const handelCloseInventoryModal = () => {
    setShowInventoryCheckModal(false);
    getValues('marshaCode') && displayHotel(currentlyAddedHotel!);
  };

  const displayHotel = (hotelResponseData: AddHotelResponse, updatedInventoryData?: UpdatedInventoryData) => {
    const miniHotelStartDate =
      isGroup && getValues('miniHotelStartDate')
        ? getFormattedDateObject(getValues('miniHotelStartDate'), 'dateWithDayMonthAndYear')
            .utc(true)
            .valueOf()
            .toString()
        : '';
    const propertyIndex = eventTrackingPropertyIndex + 1;

    append({
      ...getPropertyData(
        { ...hotelResponseData!, miniHotelStartDate },
        propertyCardLabels,
        propertyIndex
      ),
      marshaCode: getValues('marshaCode').toUpperCase() || '',
      miniHotelCode: isGroup ? getValues('miniHotelCode').toUpperCase() : undefined,
      clusterCode: isCorporate ? getValues('clusterCode').toUpperCase() : undefined,
      overriddenStartDate: updatedInventoryData?.overriddenStartDate || '',
      overriddenEndDate: updatedInventoryData?.overriddenEndDate || '',
    });

    setEventTrackingPropertyIndex(propertyIndex);
    const { isHousingProtected, availability, rates } = hotelResponseData.searchMiniHotels?.edges?.[0]?.node || {};
    if (isPickUpStatsEnabled && !isHousingProtected) {
      const eventData = getEventTrackingData(
        hotelResponseData.property.basicInformation?.name || '',
        propertyIndex,
        availability,
        rates
      );
      setEventTrackingData(eventData);
    }

    reset();
  };

  const marshaCodeField = (
    <Controller
      name="marshaCode"
      control={addHotelControl}
      rules={{
        required: marshacode.requiredError,
        pattern: {
          value: ALPHA_NUMERIC_REGEX,
          message: marshacode.invalidError,
        },
        validate: value => (value.length < 5 ? marshacode.lengthError : true),
      }}
      render={({ field }) => (
        <TextFormField
          {...field}
          label={marshacode.label}
          ariaLabel={marshacode.label}
          placeholder={marshacode.placeholder}
          maxLength={5}
          className="m-input-field"
          showErrorMessage={!!addHotelErrors?.marshaCode?.message}
          errorMessage={addHotelErrors?.marshaCode?.message}
        />
      )}
    />
  );

  const miniHotelCodeField = (
    <Controller
      name="miniHotelCode"
      control={addHotelControl}
      rules={{
        required: minihotelcode.requiredError,
        pattern: {
          value: ALPHA_NUMERIC_REGEX,
          message: minihotelcode.invalidError,
        },
        validate: value => (value.length < 3 ? minihotelcode.lengthError : true),
      }}
      render={({ field }) => (
        <TextFormField
          {...field}
          label={minihotelcode.label}
          ariaLabel={minihotelcode.label}
          placeholder={minihotelcode.placeholder}
          maxLength={3}
          className="m-input-field"
          showErrorMessage={!!addHotelErrors?.miniHotelCode?.message}
          errorMessage={addHotelErrors?.miniHotelCode?.message}
        />
      )}
    />
  );

  const miniHotelStartDateField = (
    <Controller
      name="miniHotelStartDate"
      control={addHotelControl}
      render={({ field }) => (
        <div ref={datePickerInputRef}>
          <TextFormField
            {...field}
            label={miniHotelStartDate.label}
            ariaLabel={miniHotelStartDate.label}
            className="m-input-field"
            showIcon={true}
            iconClass="icon icon-calendar"
            isIconClickable={true}
            iconClickHandler={toggleFlyout}
            showHelperText={!calendarState.isFlyoutOpen && !!miniHotelStartDate.warningMessage}
            helperText={miniHotelStartDate.warningMessage}
            onClick={toggleFlyout}
            onKeyDown={(event: React.KeyboardEvent) => {
              if (event.key === ENTER_KEY) {
                event.preventDefault();
                toggleFlyout();
              }
            }}
            onChange={event => {
              field.onChange(event);
            }}
          />
        </div>
      )}
    />
  );

  const calendar = (
    <Calendar
      ref={calendarRef}
      startDate={null}
      endDate={null}
      variation=""
      focusedInput={CALENDAR_START_DATE}
      weekdaysShort={shortWeekdayNames}
      weekdays={shortWeekdayNames}
      weekdaysLong={longWeekdayNames}
      monthsShort={shortMonthNames}
      months={longMonthNames}
      numberOfMonths={1}
      firstDayOfWeek={getLocaleValue('firstDayofWeek')}
      maxAllowedDays={CALENDAR_MAX_ALLOWED_DAYS}
      yearLabel={getLocaleValue('year') || ''}
      nightLabel=""
      nightsLabel=""
      singleDatePicker={true}
      startFromToday={true}
      disableHoverToolTip={true}
      setDateHandler={startDate => updateStartDate(startDate)}
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      setFocusedInputHandler={() => {}}
      locale={getCurrentLocale()}
    />
  );

  const clusterCodeField = (
    <Controller
      name="clusterCode"
      control={addHotelControl}
      rules={{
        required: clusterCode.requiredError,
        pattern: {
          value: ALPHA_NUMERIC_REGEX,
          message: clusterCode.invalidError,
        },
        validate: value => (value.length === 3 || value.length === 5 ? true : clusterCode.lengthError),
      }}
      render={({ field }) => (
        <TextFormField
          {...field}
          label={clusterCode.label}
          ariaLabel={clusterCode.label}
          placeholder={clusterCode.placeholder}
          maxLength={5}
          className="m-input-field"
          showErrorMessage={!!addHotelErrors?.clusterCode?.message}
          errorMessage={addHotelErrors?.clusterCode?.message}
        />
      )}
    />
  );

  const groupAddHotelForm = (
    <>
      <div className="col-lg-3 col-md-6 pb-5">{marshaCodeField}</div>
      <div className="col-lg-3 col-md-6 pb-5">{miniHotelCodeField}</div>
      <div className="col-lg-3 col-md-6 pb-5">
        {miniHotelStartDateField}
        <div ref={dropDownRef}>
          <DropDownModal
            show={calendarState.isFlyoutOpen}
            handleBlur={true}
            className="calendar-modal"
            dropdownModalOpenState={calendarState.isFlyoutOpen}
            setDropdownModalOpenState={closeFlyout}
            dropdownModalOnCLoseFunc={closeFlyout}
          >
            {calendarState.startDate && calendar}
          </DropDownModal>
        </div>
      </div>
    </>
  );

  const corporateAddHotelForm = (
    <>
      <div className="col-md-3 pb-5">{marshaCodeField}</div>
      <div className="col-md-3 pb-5">{clusterCodeField}</div>
      <div className="col-md-3 pb-5"></div>
    </>
  );

  return (
    <div>
      <Heading
        variation={Types.headingType.title}
        titleText={heading}
        fontSize={Types.size.medium}
        element={Types.tags.h3}
        customClass="mt-5"
      />
      <p className="t-font-alt-s mt-2">{helperText}</p>

      <StyledAddHotelForm>
        <div className="row">
          {isGroup ? groupAddHotelForm : corporateAddHotelForm}
          <div className="col-md-3 d-flex flex-column">
            <Button
              type={Types.ButtonTypeVariation.Button}
              className={clsx(
                'm-button-secondary add-hotel-cta',
                loading && 'm-button skeleton-loader',
                propertyCardsData.length >= RESLINK_MAX_PROPERTY_COUNT && 'disabled'
              )}
              isDisabled={loading || propertyCardsData.length >= RESLINK_MAX_PROPERTY_COUNT}
              callback={handleAddHotel}
            >
              {cta.addHotelCtaLabel}
            </Button>
            <p className="t-font-alt-s text-center mb-0 mt-3">
              {propertyCardsData.length >= RESLINK_MAX_PROPERTY_COUNT
                ? cta.propertyLimitHelperText
                : cta.addHotelHelperText.replace(
                    '{hotelNumber}',
                    `${RESLINK_MAX_PROPERTY_COUNT - propertyCardsData.length}`
                  )}
            </p>
          </div>
        </div>
      </StyledAddHotelForm>

      {apiError ? (
        <div className="mt-3">
          <InlineMessages type={InlineMessagesType.Error} title={apiFailure} severity="1" />
        </div>
      ) : null}

      {propertyCardsData.length
        ? propertyCardsData.map((propertyData, index) => {
            return (
              <div className="mt-3" key={propertyData.id}>
                <Controller
                  control={control}
                  name={`propertyCards.${index}`}
                  rules={{
                    validate: propertyCardValidator,
                  }}
                  render={() => (
                    <PropertyCard
                      propertyLabels={{
                        ...propertyCardLabels,
                        apiFailure,
                      }}
                      propertyData={{
                        ...propertyData,
                        cardNumber: index + 1,
                        totalCards: propertyCardsData.length,
                        reslinkType: reslinkType as ReservationLinkType,
                      }}
                      hasError={!!errors?.propertyCards?.[index] || (isGroup && !propertyData?.roomsData?.length)}
                      onEdit={() => handleEdit(propertyData)}
                      remove={remove}
                      update={update}
                      removeHotelLabels={{ ...removeHotelPopUp, cancelCtaLabel, confirmCtaLabel }}
                      onMoveUp={() => handleMoveUp(index)}
                      onMoveDown={() => handleMoveDown(index)}
                    />
                  )}
                />
              </div>
            );
          })
        : null}

      {showModifyDatesOccupancyModal && (
        <ModifyDatesOccupancyModal
          show={showModifyDatesOccupancyModal}
          labels={editHotelPopUp}
          defaultValues={modifyDatesOccupancyData}
          closeModal={() => setShowModifyDatesOccupancyModal(false)}
          onModify={updatedDateAndOccupancy => updateDateAndOccupancy(updatedDateAndOccupancy)}
        />
      )}

      {showInventoryCheckModal && (
        <InventoryCheckModal
          show={showInventoryCheckModal}
          labels={inventoryCheckPopUp}
          defaultValues={inventoryCheckData}
          analyticsCount={inventoryCheckCount}
          closeModal={handelCloseInventoryModal}
          onConfirmInventoryCheck={updateInventoryDateRange}
        />
      )}
    </div>
  );
};
